import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import IconShow from "canvas-core-react/lib/IconShow";
import IconEdit from "canvas-core-react/lib/IconEdit";
import IconDelete from "canvas-core-react/lib/IconDelete";
import ActionMenuList from "canvas-core-react/lib/ActionMenuList";
import ActionMenuListItem from "canvas-core-react/lib/ActionMenuListItem";

import useApi from "../../../hooks/useApi";
import Table from "../../../shared/table";
import Modal from "../../../utils/customModal";
import {
  enableScroll,
  loadingHtml,
  moduleAction,
} from "../../../utils/functions";
import plusImage from "../../../../../static/images/plus.svg";
import { MOD_CUSTOM, WRITE } from "../../../../permissions/";
import { getRoles } from "../../../../Redux/reducer/session";
import ProviderAccountDetail from "./ProviderAccountDetail";
import ProviderNewAccount from "./ProviderNewAccount";
import useModal from "../../../hooks/useModal";
import DeleteConfirmModal from "./DeleteAccountModal";
import { LABELS_ACCOUNT_TYPE } from "../../../shared/AccountTypeSelect";
import Status from "../../../shared/status";

const ProviderAccount = ({ provider }) => {
  const { t } = useTranslation();
  const { show, onToggle } = useModal();
  const { getApiCAll, cancellCall } = useApi();
  const [data, setData] = useState({});
  const [menu, setMenu] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newAccount, setNewAccount] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, sizePerPage: 10 });
  const roles = useSelector((state) => getRoles(state));

  const getList = async ({ page, sizePerPage, querystring = null }) => {
    setLoading(true);
    const queryString = querystring !== null ? querystring : query;

    try {
      const response = await getApiCAll(
        `/providers/provider-account-list/${provider.id}?page=${page}&size=${sizePerPage}${queryString}`
      );

      const { data } = response.data;

      setData(data);
      setQuery(queryString);
      setPagination({ page, sizePerPage });
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  };

  const handleMenu = (id, open) => {
    const tmpMenu = [...menu];
    if (open === false) {
      setMenu([]);
    } else {
      tmpMenu[`menu-${id}`] = open;
      setMenu(tmpMenu);
    }
  };

  const getData = () => {
    return (
      data.content?.map((item) => ({
        country: item?.coutryCodeCompany,
        requestType: LABELS_ACCOUNT_TYPE[item?.accountType],
        divisa: item?.accountCurrency,
        providerAccount: item?.accountNumber,
        status: <Status statusCode={item.status} />,
        item,
      })) || []
    );
  };

  const getHeaders = () => {
    return [
      { selector: "country", name: t("providers.table.headers.country") },
      {
        selector: "requestType",
        name: t("providers.table.headers.requestType"),
      },
      { selector: "divisa", name: t("providers.table.headers.divisa") },
      {
        selector: "providerAccount",
        name: t("providers.table.headers.providerAccount"),
      },
      {
        selector: "status",
        name: t("providers.table.headers.state"),
      },
      {
        selector: "actions",
        name: t("providers.table.headers.action"),
        center: true,
        cellFormatter: (row) => (
          <ActionMenuList
            dialogPosition="bottom-right"
            iconType="horizontal-small"
            bottomSheet={{ heading: "Actions" }}
            isMenuOpen={menu[`menu-${row.item.id}`] || false}
            setIsMenuOpen={(open) => handleMenu(row.item.id, open)}
          >
            <ActionMenuListItem
              className="button-show-more"
              icon={() => (
                <IconShow
                  id={`related-${row.item.id}`}
                  className="TextButton__icon--left"
                />
              )}
              onClick={() => {
                setMenu([]);
                setIsEditing(false);
                setShowDetail(true);
                setSelectedAccount(row.item);
                enableScroll();
              }}
            >
              {t("general.labels.fileModal.detail")}
            </ActionMenuListItem>

            {moduleAction(roles, MOD_CUSTOM, WRITE) && (
              <ActionMenuListItem
                className="button-show-edit"
                icon={() => (
                  <IconEdit
                    id={`edit-${row.item.id}`}
                    className="TextButton__icon--left"
                  />
                )}
                onClick={() => {
                  setSelectedAccount(row.item);
                  setMenu([]);
                  setIsEditing(true);
                  setShowDetail(true);
                  enableScroll();
                }}
              >
                {t("general.labels.edit")}
              </ActionMenuListItem>
            )}

            {moduleAction(roles, MOD_CUSTOM, WRITE) && (
              <ActionMenuListItem
                className="button-show-remove"
                icon={() => (
                  <IconDelete
                    id={`remove-${row.item.id}`}
                    className="TextButton__icon--left"
                  />
                )}
                onClick={() => {
                  setSelectedAccount(row.item);
                  setMenu([]);
                  onToggle();
                  enableScroll();
                }}
              >
                {t("general.provider_account_detail.delete")}
              </ActionMenuListItem>
            )}
          </ActionMenuList>
        ),
      },
    ];
  };

  const handleBack = () => {
    setNewAccount(false);
    setShowDetail(false);
  };

  const showNewAccount = () => {
    setShowDetail(true);
    setNewAccount(true);
  };

  const onTableChange = ({ page, sizePerPage }) => {
    setPagination({ page, sizePerPage });
    return getList({ page, sizePerPage });
  };

  useEffect(() => {
    const { page, sizePerPage } = pagination;
    getList({ page, sizePerPage });

    return cancellCall;
  }, []);

  if (newAccount)
    return (
      <div>
        <ProviderNewAccount
          doBack={handleBack}
          providerId={provider.id}
          countryCode={provider.country.code}
        />
      </div>
    );

  if (showDetail) {
    return (
      <div>
        <ProviderAccountDetail
          isEditing={isEditing}
          doBack={handleBack}
          data={selectedAccount}
        />
      </div>
    );
  }

  return (
    <div>
      {moduleAction(roles, MOD_CUSTOM, WRITE) ? (
        <div className="custom-flex-space">
          <h3 className="flex-element"></h3>
          <div className="flex-element custom-flex-space">
            <p className="add-account mt0" onClick={showNewAccount}>
              {t(`general.provider_account_detail.add_new_account`)}
            </p>
            <img
              className="icon-plus--account mt0"
              onClick={showNewAccount}
              src={plusImage}
            />
          </div>
        </div>
      ) : null}

      <Table
        data={getData()}
        page={pagination.page}
        headers={getHeaders()}
        totalSize={data.totalElements}
        onTableChange={onTableChange}
        sizePerPage={pagination.sizePerPage}
      />

      <DeleteConfirmModal
        show={show}
        onOk={onToggle}
        onCancel={onToggle}
        accountId={selectedAccount?.id}
        providerId={selectedAccount?.providerId}
      />

      <Modal show={loading}>{loadingHtml()}</Modal>
    </div>
  );
};

export default ProviderAccount;
